/**
 * BreadcrumbList JSON-LD helper.
 *
 * Use across detail / list / blog pages to expose breadcrumb hierarchy to
 * search engines (rich-result eligible). The last item should be the current
 * page and omit `url` — that signals "you are here" to crawlers.
 */
import { useMemo } from "react";

export interface BreadcrumbItem {
  name: string;
  url?: string;
}

export interface BreadcrumbLdProps {
  items: BreadcrumbItem[];
}

function buildLd(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => {
      const node: Record<string, unknown> = {
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
      };
      if (it.url) node.item = it.url;
      return node;
    }),
  };
}

export function BreadcrumbLd({ items }: BreadcrumbLdProps) {
  const json = useMemo(() => JSON.stringify(buildLd(items)), [items]);
  if (items.length === 0) return null;
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: json }}
    />
  );
}

export function buildBreadcrumbLd(items: BreadcrumbItem[]) {
  return buildLd(items);
}

const SITE_ORIGIN = "https://majourneys.com";

export function siteUrl(lang: string, path: string = ""): string {
  const cleaned = path.startsWith("/") ? path : path ? `/${path}` : "";
  return `${SITE_ORIGIN}/${lang}${cleaned}`;
}
